import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Grid,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { createPipeline } from '../api/client';
import { getS3Buckets, getBucketFolders } from '../api/s3';
import { getTouchstreamConfigs, getTouchstreamStreams, getTouchstreamStreamOptions } from '../api/touchstream';

function CreatePipelineForm({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'aws',
    aws_role_arn: 'arn:aws:iam::714125606834:role/MediaLiveControlRoomRole',
    aws_region: 'us-east-1',
    creator_username: '',
    encoding: {
      video_bitrates: [
        { name: 'high', bitrate: 5000000, width: 1920, height: 1080 },
        { name: 'medium', bitrate: 2500000, width: 1280, height: 720 },
        { name: 'low', bitrate: 1000000, width: 854, height: 480 }
      ],
      audio_bitrates: [
        { name: 'main', bitrate: 128000, sample_rate: 48000 }
      ]
    },
    // S3 pipeline fields
    s3_bucket_id: '',
    s3_content_folder: '',
    // Touchstream pipeline fields
    touchstream_config_id: '',
    touchstream_stream_keys: [],
    hls_url: '',
    header_check_key: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // S3 state
  const [s3Buckets, setS3Buckets] = useState([]);
  const [bucketFolders, setBucketFolders] = useState([]);
  const [loadingBuckets, setLoadingBuckets] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);
  // Touchstream state
  const [touchstreamConfigs, setTouchstreamConfigs] = useState([]);
  const [touchstreamStreams, setTouchstreamStreams] = useState([]);
  const [loadingStreams, setLoadingStreams] = useState(false);
  const [headerGroupOptions, setHeaderGroupOptions] = useState([]);

  // Load S3 buckets and Touchstream configs on mount
  useEffect(() => {
    const loadBuckets = async () => {
      try {
        setLoadingBuckets(true);
        const buckets = await getS3Buckets();
        setS3Buckets(buckets);
      } catch (err) {
        console.error('Failed to load S3 buckets:', err);
      } finally {
        setLoadingBuckets(false);
      }
    };
    const loadTouchstreamConfigs = async () => {
      try {
        const configs = await getTouchstreamConfigs();
        setTouchstreamConfigs(configs);
      } catch (err) {
        console.error('Failed to load Touchstream configs:', err);
      }
    };
    loadBuckets();
    loadTouchstreamConfigs();
  }, []);

  const loadStreamsForConfig = async (configId) => {
    setLoadingStreams(true);
    try {
      const [streamsData, optionsData] = await Promise.all([
        getTouchstreamStreams(configId),
        getTouchstreamStreamOptions(configId)
      ]);
      setTouchstreamStreams(streamsData.streams || []);
      const headerGroupChoices = optionsData?.options?.actions?.POST?.header_group?.choices || [];
      setHeaderGroupOptions(headerGroupChoices);
    } catch (err) {
      console.error('Failed to load streams:', err);
      setTouchstreamStreams([]);
      setHeaderGroupOptions([]);
    } finally {
      setLoadingStreams(false);
    }
  };

  // Load bucket folders when bucket changes
  useEffect(() => {
    if (formData.s3_bucket_id) {
      const loadFolders = async () => {
        try {
          setLoadingFolders(true);
          const data = await getBucketFolders(formData.s3_bucket_id);
          setBucketFolders(data.folders || []);
        } catch (err) {
          console.error('Failed to load bucket folders:', err);
          setBucketFolders([]);
        } finally {
          setLoadingFolders(false);
        }
      };
      loadFolders();
    } else {
      setBucketFolders([]);
    }
  }, [formData.s3_bucket_id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleVideoBitrateChange = (index, field, value) => {
    const newVideoBitrates = [...formData.encoding.video_bitrates];
    newVideoBitrates[index][field] = field === 'bitrate' || field === 'width' || field === 'height'
      ? parseInt(value) || 0
      : value;
    setFormData({
      ...formData,
      encoding: {
        ...formData.encoding,
        video_bitrates: newVideoBitrates
      }
    });
  };

  const handleAudioBitrateChange = (index, field, value) => {
    const newAudioBitrates = [...formData.encoding.audio_bitrates];
    newAudioBitrates[index][field] = field === 'bitrate' || field === 'sample_rate'
      ? parseInt(value) || 0
      : value;
    setFormData({
      ...formData,
      encoding: {
        ...formData.encoding,
        audio_bitrates: newAudioBitrates
      }
    });
  };

  const addVideoBitrate = () => {
    setFormData({
      ...formData,
      encoding: {
        ...formData.encoding,
        video_bitrates: [
          ...formData.encoding.video_bitrates,
          { name: `custom_${formData.encoding.video_bitrates.length + 1}`, bitrate: 1500000, width: 1280, height: 720 }
        ]
      }
    });
  };

  const removeVideoBitrate = (index) => {
    if (formData.encoding.video_bitrates.length > 1) {
      const newVideoBitrates = formData.encoding.video_bitrates.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        encoding: {
          ...formData.encoding,
          video_bitrates: newVideoBitrates
        }
      });
    }
  };

  const addAudioBitrate = () => {
    setFormData({
      ...formData,
      encoding: {
        ...formData.encoding,
        audio_bitrates: [
          ...formData.encoding.audio_bitrates,
          { name: `audio_${formData.encoding.audio_bitrates.length + 1}`, bitrate: 96000, sample_rate: 48000 }
        ]
      }
    });
  };

  const removeAudioBitrate = (index) => {
    if (formData.encoding.audio_bitrates.length > 1) {
      const newAudioBitrates = formData.encoding.audio_bitrates.filter((_, i) => i !== index);
      setFormData({
        ...formData,
        encoding: {
          ...formData.encoding,
          audio_bitrates: newAudioBitrates
        }
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Clean up formData - remove empty strings for optional UUID fields
      const cleanedData = { ...formData };
      if (!cleanedData.s3_bucket_id) {
        delete cleanedData.s3_bucket_id;
      }
      if (!cleanedData.s3_content_folder) {
        delete cleanedData.s3_content_folder;
      }
      if (!cleanedData.touchstream_config_id) {
        delete cleanedData.touchstream_config_id;
      }
      if (!cleanedData.touchstream_stream_keys || cleanedData.touchstream_stream_keys.length === 0) {
        delete cleanedData.touchstream_stream_keys;
      }
      if (cleanedData.type !== 'touchstream') {
        delete cleanedData.hls_url;
        delete cleanedData.header_check_key;
      } else if (!cleanedData.header_check_key) {
        delete cleanedData.header_check_key;
      }

      await createPipeline(cleanedData);
      onSuccess();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Handle Pydantic validation errors which come as array of objects
      if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg).join(', '));
      } else {
        setError(detail || 'Failed to create pipeline');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle>Create New Pipeline</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Pipeline Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
              autoFocus
            />

            <TextField
              label="Your Username"
              name="creator_username"
              value={formData.creator_username}
              onChange={handleChange}
              required
              fullWidth
              helperText="This will be used to tag AWS resources for cost tracking"
              placeholder="john.doe"
            />

            <FormControl fullWidth>
              <InputLabel>Pipeline Type</InputLabel>
              <Select
                name="type"
                value={formData.type}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    type: e.target.value,
                    s3_bucket_id: '',
                    s3_content_folder: '',
                    touchstream_config_id: '',
                    touchstream_stream_keys: [],
                    hls_url: '',
                    header_check_key: ''
                  });
                  setTouchstreamStreams([]);
                  setHeaderGroupOptions([]);
                }}
                label="Pipeline Type"
              >
                <MenuItem value="aws">AWS Media Services</MenuItem>
                <MenuItem value="norsk">Norsk (Docker)</MenuItem>
                <MenuItem value="s3">S3 VOD</MenuItem>
                <MenuItem value="touchstream">Touchstream Only</MenuItem>
              </Select>
              <FormHelperText>
                {formData.type === 's3' && 'S3 pipelines serve pre-packaged HLS/DASH content'}
                {formData.type === 'touchstream' && 'Manages Touchstream streams without infrastructure'}
              </FormHelperText>
            </FormControl>

            {formData.type === 'aws' && (
              <>
                <TextField
                  label="AWS Role ARN"
                  name="aws_role_arn"
                  value={formData.aws_role_arn}
                  onChange={handleChange}
                  placeholder="arn:aws:iam::123456789012:role/MediaServicesRole"
                  fullWidth
                  helperText="IAM role with permissions for MediaLive, MediaPackage, and CloudFront"
                />

                <FormControl fullWidth>
                  <InputLabel>AWS Region</InputLabel>
                  <Select
                    name="aws_region"
                    value={formData.aws_region}
                    onChange={handleChange}
                    label="AWS Region"
                  >
                    <MenuItem value="us-east-1">US East (N. Virginia)</MenuItem>
                    <MenuItem value="us-west-2">US West (Oregon)</MenuItem>
                    <MenuItem value="eu-west-1">EU (Ireland)</MenuItem>
                    <MenuItem value="eu-central-1">EU (Frankfurt)</MenuItem>
                    <MenuItem value="ap-southeast-1">Asia Pacific (Singapore)</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            {formData.type === 's3' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>S3 Bucket</InputLabel>
                  <Select
                    name="s3_bucket_id"
                    value={formData.s3_bucket_id}
                    onChange={handleChange}
                    label="S3 Bucket"
                    disabled={loadingBuckets}
                  >
                    {loadingBuckets ? (
                      <MenuItem disabled>Loading buckets...</MenuItem>
                    ) : s3Buckets.length === 0 ? (
                      <MenuItem disabled>No buckets configured - add one in S3 Buckets tab</MenuItem>
                    ) : (
                      s3Buckets.map((bucket) => (
                        <MenuItem key={bucket.id} value={bucket.id}>
                          {bucket.name} ({bucket.region})
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  <FormHelperText>
                    Select an S3 bucket containing HLS/DASH content
                  </FormHelperText>
                </FormControl>

                {formData.s3_bucket_id && (
                  <FormControl fullWidth>
                    <InputLabel>Content Folder</InputLabel>
                    <Select
                      name="s3_content_folder"
                      value={formData.s3_content_folder}
                      onChange={handleChange}
                      label="Content Folder"
                      disabled={loadingFolders}
                    >
                      {loadingFolders ? (
                        <MenuItem disabled>Loading folders...</MenuItem>
                      ) : bucketFolders.length === 0 ? (
                        <MenuItem disabled>No content folders found - upload HLS/DASH content first</MenuItem>
                      ) : (
                        bucketFolders.map((folder) => (
                          <MenuItem key={folder} value={folder}>
                            {folder}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    <FormHelperText>
                      Select the folder containing the HLS/DASH manifest
                    </FormHelperText>
                  </FormControl>
                )}
              </>
            )}

            {formData.type === 'touchstream' && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Touchstream Config</InputLabel>
                  <Select
                    value={formData.touchstream_config_id}
                    onChange={async (e) => {
                      const configId = e.target.value;
                      setFormData({
                        ...formData,
                        touchstream_config_id: configId,
                        touchstream_stream_keys: [],
                        header_check_key: ''
                      });
                      if (configId) {
                        await loadStreamsForConfig(configId);
                      } else {
                        setTouchstreamStreams([]);
                        setHeaderGroupOptions([]);
                      }
                    }}
                    label="Touchstream Config"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {touchstreamConfigs.map((config) => (
                      <MenuItem key={config.id} value={config.id}>
                        {config.name} ({config.context_type})
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select a Touchstream configuration to manage</FormHelperText>
                </FormControl>

                {formData.touchstream_config_id && (
                  <FormControl fullWidth>
                    <InputLabel>Stream Keys</InputLabel>
                    <Select
                      multiple
                      value={formData.touchstream_stream_keys}
                      onChange={(e) => {
                        const { value } = e.target;
                        setFormData({
                          ...formData,
                          touchstream_stream_keys: typeof value === 'string' ? value.split(',') : value
                        });
                      }}
                      label="Stream Keys"
                      disabled={loadingStreams}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((key) => {
                            const stream = touchstreamStreams.find(s => s.key === key);
                            return (
                              <Chip
                                key={key}
                                label={stream?.name || key}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {loadingStreams ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Loading streams...
                        </MenuItem>
                      ) : touchstreamStreams.length === 0 ? (
                        <MenuItem disabled>No streams available</MenuItem>
                      ) : (
                        touchstreamStreams.map((stream) => (
                          <MenuItem key={stream.key} value={stream.key}>
                            {stream.name || stream.key} ({stream.stream_format || 'HLS'} - {stream.stream_cdn || 'CDN'})
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    <FormHelperText>
                      Select streams to manage with this pipeline
                    </FormHelperText>
                  </FormControl>
                )}

                <TextField
                  label="Manifest URL"
                  name="hls_url"
                  value={formData.hls_url}
                  onChange={(e) => setFormData({ ...formData, hls_url: e.target.value })}
                  required
                  fullWidth
                  helperText="Playback/manifest URL shown on the pipeline card"
                />

                {formData.touchstream_config_id && headerGroupOptions.length > 0 && (
                  <FormControl fullWidth>
                    <InputLabel>Header Check</InputLabel>
                    <Select
                      value={formData.header_check_key}
                      onChange={(e) => setFormData({ ...formData, header_check_key: e.target.value })}
                      label="Header Check"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {headerGroupOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.display_name} ({option.value})
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select a header group key for Header Check error simulation
                    </FormHelperText>
                  </FormControl>
                )}
              </>
            )}

            {/* Encoding Configuration - Available for AWS and Norsk, not S3 or Touchstream */}
            {formData.type !== 's3' && formData.type !== 'touchstream' && (
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Encoding Configuration</Typography>
                <Chip
                  label={`${formData.encoding.video_bitrates.length} video + ${formData.encoding.audio_bitrates.length} audio`}
                  size="small"
                  sx={{ ml: 2 }}
                />
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Video Bitrates */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">Video Bitrates</Typography>
                      <IconButton onClick={addVideoBitrate} size="small" sx={{ ml: 1 }}>
                        <AddIcon />
                      </IconButton>
                    </Box>
                    {formData.encoding.video_bitrates.map((video, index) => (
                      <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6} md={2}>
                            <TextField
                              label="Name"
                              value={video.name}
                              onChange={(e) => handleVideoBitrateChange(index, 'name', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Bitrate (bps)"
                              type="number"
                              value={video.bitrate}
                              onChange={(e) => handleVideoBitrateChange(index, 'bitrate', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6} sm={4} md={2}>
                            <TextField
                              label="Width"
                              type="number"
                              value={video.width}
                              onChange={(e) => handleVideoBitrateChange(index, 'width', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6} sm={4} md={2}>
                            <TextField
                              label="Height"
                              type="number"
                              value={video.height}
                              onChange={(e) => handleVideoBitrateChange(index, 'height', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6} sm={3} md={2}>
                            <Chip
                              label={`${(video.bitrate / 1000000).toFixed(1)}M`}
                              size="small"
                              color="primary"
                            />
                          </Grid>
                          <Grid item xs={6} sm={1} md={1}>
                            {formData.encoding.video_bitrates.length > 1 && (
                              <IconButton
                                onClick={() => removeVideoBitrate(index)}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>

                  {/* Audio Bitrates */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1">Audio Bitrates</Typography>
                      <IconButton onClick={addAudioBitrate} size="small" sx={{ ml: 1 }}>
                        <AddIcon />
                      </IconButton>
                    </Box>
                    {formData.encoding.audio_bitrates.map((audio, index) => (
                      <Box key={index} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Name"
                              value={audio.name}
                              onChange={(e) => handleAudioBitrateChange(index, 'name', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Bitrate (bps)"
                              type="number"
                              value={audio.bitrate}
                              onChange={(e) => handleAudioBitrateChange(index, 'bitrate', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={3}>
                            <TextField
                              label="Sample Rate (Hz)"
                              type="number"
                              value={audio.sample_rate}
                              onChange={(e) => handleAudioBitrateChange(index, 'sample_rate', e.target.value)}
                              size="small"
                              fullWidth
                            />
                          </Grid>
                          <Grid item xs={6} sm={3} md={2}>
                            <Chip
                              label={`${(audio.bitrate / 1000)}k`}
                              size="small"
                              color="secondary"
                            />
                          </Grid>
                          <Grid item xs={6} sm={3} md={1}>
                            {formData.encoding.audio_bitrates.length > 1 && (
                              <IconButton
                                onClick={() => removeAudioBitrate(index)}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              loading ||
              !formData.name ||
              !formData.creator_username ||
              (formData.type === 's3' && (!formData.s3_bucket_id || !formData.s3_content_folder)) ||
              (formData.type === 'touchstream' && (!formData.touchstream_config_id || formData.touchstream_stream_keys.length === 0 || !formData.hls_url))
            }
          >
            {loading ? 'Creating...' : 'Create Pipeline'}
          </Button>
        </DialogActions>
      </form>
    </>
  );
}

export default CreatePipelineForm;